import { motion } from "framer-motion";
import { Sofa, Warehouse, TreePine, Hammer, Waves, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/site/Reveal";

const SERVICES = [
  {
    icon: Sofa,
    title: "Furniture & Appliance Removal",
    body: "Couches, mattresses, fridges, washers — we lift, load, and haul it all so you don't have to.",
    tag: "MOST REQUESTED",
  },
  {
    icon: Warehouse,
    title: "Garage, Estate & House Cleanouts",
    body: "Full property clean-outs handled with care and speed. From packed garages to whole estates.",
    tag: "FULL SERVICE",
  },
  {
    icon: TreePine,
    title: "Yard & Construction Debris",
    body: "Storm limbs, brush piles, remodel scrap, and job-site rubble — cleared fast, disposed of right.",
    tag: "HEAVY DUTY",
  },
  {
    icon: Hammer,
    title: "Shed Demolition",
    body: "That leaning shed in the backyard? We'll tear it down, haul it off, and rake the spot clean.",
    tag: "TEAR-DOWN",
  },
  {
    icon: Waves,
    title: "Hot Tub Removal",
    body: "Old spas and hot tubs are no match for our crew. Disconnected, broken down, and gone.",
    tag: "SPECIALTY",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative border-y border-[#282C34] bg-[#0e1013] py-28 sm:py-36"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono-ind text-xs uppercase tracking-[0.3em] text-[#FF9F00]">
              What We Haul
            </p>
            <h2 className="mt-4 font-display text-5xl leading-none tracking-tight sm:text-7xl">
              SERVICES <span className="text-stroke-orange">OFFERED</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-zinc-400">
            Big or small, we do it all. Every job includes loading, hauling, and
            responsible disposal — recycle and donate whenever possible.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-px border border-[#282C34] bg-[#282C34] sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              data-testid={`service-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              className="group relative bg-[#14171C] p-8 transition-colors duration-500 hover:bg-[#181c22]"
            >
              <div className="flex items-start justify-between">
                <span className="flex h-14 w-14 items-center justify-center border border-[#282C34] bg-[#0B0C0E] text-[#FF9F00] transition-all duration-500 group-hover:border-[#FF5A00] group-hover:text-[#FF5A00]">
                  <s.icon className="h-6 w-6" strokeWidth={1.75} />
                </span>
                <span className="font-mono-ind text-[10px] uppercase tracking-[0.2em] text-zinc-600 group-hover:text-[#FF9F00]">
                  {s.tag}
                </span>
              </div>
              <h3 className="mt-8 font-display text-2xl tracking-wide text-zinc-100 sm:text-3xl">
                {s.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.body}</p>
              <a
                href="#quote"
                data-testid={`service-quote-link-${i}`}
                onClick={(e) => {
                  e.preventDefault();
                  window.__lenis
                    ? window.__lenis.scrollTo("#quote", { offset: -72 })
                    : document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="mt-6 inline-flex items-center gap-2 font-mono-ind text-xs uppercase tracking-widest text-zinc-500 transition-colors duration-300 group-hover:text-[#FF9F00]"
              >
                Get a quote
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-[#FF5A00] transition-all duration-500 group-hover:w-full" />
            </motion.div>
          ))}

          <motion.a
            href="tel:+19183997313"
            data-testid="services-call-card"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="group relative flex flex-col justify-between overflow-hidden bg-[#FF5A00] p-8 text-[#0B0C0E]"
          >
            <span className="font-mono-ind text-[10px] uppercase tracking-[0.2em]">
              Don't see your job listed?
            </span>
            <div className="mt-10">
              <p className="font-display text-3xl leading-tight tracking-wide sm:text-4xl">
                IF IT'S JUNK,
                <br />
                WE HAUL IT.
              </p>
              <p className="mt-4 flex items-center gap-2 font-mono-ind text-sm font-bold tracking-widest">
                CALL 918-399-7313
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </p>
            </div>
            <span className="hazard absolute bottom-0 left-0 right-0 h-2 opacity-60" />
          </motion.a>
        </div>
      </div>
    </section>
  );
}
